import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Database, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Eye, Search, GripVertical, Archive, Undo2, History, AlertTriangle, Activity } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

const SLUG_PATTERN = /^[a-z0-9]{3,16}$/;
const HISTORY_POLL_INTERVAL_MS = 10000;  // background poll for live status of in-flight jobs
const HARD_DELETE_POLL_INTERVAL_MS = 2000;  // faster during the focused wait of a single delete

// Map a provisioning record's status to an STUI badge class.
// Mirrors the semantic colors used by WfStatusRow in RevLogPage.
const statusBadgeClass = (status) => {
  switch (status) {
    case 'queued':    return 'stui-badge-pending';       // yellow
    case 'running':   return 'stui-badge-construction';  // orange
    case 'succeeded': return 'stui-badge-accepted';      // green
    case 'failed':    return 'stui-badge-none';          // red
    default:          return 'stui-badge-expired';       // gray fallback
  }
};

// Environment-aware DB name prefix. Backend uses Provisioning:DatabaseNamePrefix config; frontend derives from hostname.
// dawn.wmeng.com → prod; everything else (ppdawn, localhost, dev VMs) → preprod.
const DB_NAME_PREFIX = (typeof window !== 'undefined' && window.location.hostname === 'dawn.wmeng.com')
  ? 'pmd-prod-db-'
  : 'pmd-preprod-db-';

const DatabasesPage = () => {
  const [databases, setDatabases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Hard delete modal (kept for friction)
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState('');
  const [hardDeleteJob, setHardDeleteJob] = useState(null);
  const [hardDeleteError, setHardDeleteError] = useState(null);

  // History modal — also serves as async status view for in-flight provisioning jobs
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLastChecked, setHistoryLastChecked] = useState(null);
  // When auto-opened after provisioning, track the record so we can auto-close on completion
  const [trackedProvisionId, setTrackedProvisionId] = useState(null);

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dbRes, orgRes] = await Promise.all([
        adminManagementApi.getDatabases(),
        adminManagementApi.getOrganizations()
      ]);
      setDatabases(dbRes.data);
      setOrganizations(orgRes.data || []);
    }
    catch (err) { console.error('Failed to load databases:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setHistoryLoading(true);
    try {
      const res = await adminManagementApi.getProvisioningHistory(50);
      setHistory(res.data || []);
      setHistoryLastChecked(new Date());
    }
    catch (err) { console.error('Failed to load history:', err); }
    finally { if (!silent) setHistoryLoading(false); }
  }, []);

  // Any in-flight provisioning work — drives the heartbeat pulse on the kebab + the polling indicator
  const anyProvisioningInFlight = history.some(r => r.status === 'queued' || r.status === 'running');

  // Load history on mount so the kebab pulse is accurate even before the modal opens
  useEffect(() => { loadHistory({ silent: true }); }, [loadHistory]);

  // Background poll while any job is running. Runs whether the modal is open or not.
  // Also auto-close the modal + refresh DB list when the job we're tracking hits a terminal state.
  useEffect(() => {
    if (trackedProvisionId != null && historyOpen) {
      const tracked = history.find(r => r.id === trackedProvisionId);
      if (tracked && (tracked.status === 'succeeded' || tracked.status === 'failed')) {
        load();
        // Both state clears must happen inside the timeout — clearing trackedProvisionId
        // synchronously would re-trigger this effect and clearTimeout our own close.
        const t = setTimeout(() => {
          setHistoryOpen(false);
          setTrackedProvisionId(null);
        }, 1500);
        return () => clearTimeout(t);
      }
    }

    if (!anyProvisioningInFlight) return;
    const t = setInterval(() => { loadHistory({ silent: true }); load(); }, HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [historyOpen, history, anyProvisioningInFlight, trackedProvisionId, loadHistory, load]);

  // ===== In-place edit / add =====
  const startEdit = () => {
    if (!selected) return;
    setEditing({
      ID: selected.id,
      DisplayName: selected.displayName,
      Slug: selected.slug || '',
      Description: selected.description || '',
      IsActive: selected.isActive,
      IsSysAdminOnly: selected.isSysAdminOnly ?? false,
      OrganizationId: selected.organizationId || null
    });
    setDirtyFields(new Set());
  };

  const startAdd = () => {
    const wmeOrg = organizations.find(o => o.displayName?.toLowerCase().includes('wunderlich') || o.displayName?.toLowerCase() === 'wme');
    setSelected(null);
    setEditing({
      ID: null,
      DisplayName: '',
      Slug: '',
      Description: '',
      IsActive: true,
      IsSysAdminOnly: false,
      OrganizationId: wmeOrg ? wmeOrg.id : null
    });
    setDirtyFields(new Set(['DisplayName', 'Slug']));
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) { notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); }); return; }
    setEditing(null); setDirtyFields(new Set());
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const origMap = editing?.ID ? {
      DisplayName: selected?.displayName,
      Slug: selected?.slug || '',
      Description: selected?.description || '',
      IsActive: selected?.isActive,
      IsSysAdminOnly: selected?.isSysAdminOnly ?? false,
      OrganizationId: selected?.organizationId || null
    } : {};
    const newDirty = new Set(dirtyFields);
    if (!editing?.ID || value !== origMap[field]) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    setSaving(true);
    try {
      if (editing.ID) {
        // Update existing — plain PUT
        const payload = {
          ID: editing.ID,
          DatabaseName: selected.databaseName,
          DisplayName: editing.DisplayName,
          Description: editing.Description,
          IsActive: editing.IsActive,
          IsSysAdminOnly: editing.IsSysAdminOnly,
          OrganizationId: editing.OrganizationId,
          Slug: selected.slug
        };
        await adminManagementApi.updateDatabase(editing.ID, payload);
        setEditing(null); setDirtyFields(new Set());
        const res = await adminManagementApi.getDatabases();
        setDatabases(res.data);
        const refreshed = res.data.find(d => d.id === editing.ID);
        if (refreshed) setSelected(refreshed);
        notify.success('Saved');
      } else {
        // Provision new — POST triggers async job. Kick off + open History for status.
        if (!SLUG_PATTERN.test(editing.Slug)) {
          notify.error('Slug must be 3–16 lowercase letters/digits');
          setSaving(false);
          return;
        }
        if (!editing.DisplayName.trim()) {
          notify.error('Display Name is required');
          setSaving(false);
          return;
        }
        if (!editing.OrganizationId) {
          notify.error('Organization is required');
          setSaving(false);
          return;
        }
        const res = await adminManagementApi.provisionDatabase({
          displayName: editing.DisplayName.trim(),
          slug: editing.Slug,
          description: editing.Description?.trim() || null,
          organizationId: parseInt(editing.OrganizationId, 10),
          isActive: editing.IsActive,
          isSysAdminOnly: editing.IsSysAdminOnly
        });
        setEditing(null); setDirtyFields(new Set());
        notify.success(`Provisioning started for ${DB_NAME_PREFIX}${editing.Slug}`);
        setTrackedProvisionId(res.data.recordId);
        setHistoryOpen(true);
        loadHistory();
      }
    } catch (err) {
      console.error('Save failed:', err);
      notify.error('Save failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ===== Decommission / Restore (via kebab) =====
  const handleDecommission = () => {
    if (!selected) return;
    setMenuOpen(false);
    notify.confirm(
      `Decommission "${selected.displayName}"? The Azure database stays alive — you can Restore or Permanently Delete later.`,
      async () => {
        try {
          await adminManagementApi.decommissionDatabase(selected.id);
          notify.success(`${selected.displayName} decommissioned`);
          await load();
          const refreshed = (await adminManagementApi.getDatabases()).data.find(d => d.id === selected.id);
          if (refreshed) setSelected(refreshed);
        } catch (err) {
          notify.error(err.response?.data?.error || err.message || 'Decommission failed');
        }
      }
    );
  };

  const handleRestore = async () => {
    if (!selected) return;
    setMenuOpen(false);
    try {
      await adminManagementApi.restoreDatabase(selected.id);
      notify.success(`${selected.displayName} restored — set Active to bring it back online`);
      await load();
      const refreshed = (await adminManagementApi.getDatabases()).data.find(d => d.id === selected.id);
      if (refreshed) setSelected(refreshed);
    } catch (err) {
      notify.error(err.response?.data?.error || err.message || 'Restore failed');
    }
  };

  // ===== Hard delete (modal kept for friction) =====
  const openHardDelete = () => {
    if (!selected) return;
    setMenuOpen(false);
    setHardDeleteTarget(selected);
    setHardDeleteConfirm('');
    setHardDeleteJob(null);
    setHardDeleteError(null);
    setHardDeleteOpen(true);
  };

  const submitHardDelete = async () => {
    setHardDeleteError(null);
    if (hardDeleteConfirm !== hardDeleteTarget.databaseName) {
      setHardDeleteError(`Type "${hardDeleteTarget.databaseName}" exactly to confirm`);
      return;
    }
    try {
      const res = await adminManagementApi.hardDeleteDatabase(hardDeleteTarget.id, { confirmDatabaseName: hardDeleteConfirm });
      setHardDeleteJob({ id: res.data.recordId, status: 'queued' });
    } catch (err) {
      setHardDeleteError(err.response?.data?.error || err.message || 'Hard delete request failed');
    }
  };

  useEffect(() => {
    if (!hardDeleteJob || hardDeleteJob.status === 'succeeded' || hardDeleteJob.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const res = await adminManagementApi.getProvisioningRecord(hardDeleteJob.id);
        setHardDeleteJob(res.data);
        if (res.data.status === 'succeeded') { notify.success(`Permanently deleted: ${res.data.databaseName}`); await load(); setSelected(null); }
        else if (res.data.status === 'failed') { notify.error(`Delete failed: ${res.data.errorMessage || 'unknown error'}`); }
      } catch (err) { console.error('Poll failed:', err); }
    }, HARD_DELETE_POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hardDeleteJob?.id, hardDeleteJob?.status, load, notify]);

  const closeHardDelete = () => { setHardDeleteOpen(false); setHardDeleteTarget(null); setHardDeleteJob(null); setHardDeleteError(null); };

  // ===== History modal =====
  const openHistory = () => { setMenuOpen(false); setTrackedProvisionId(null); setHistoryOpen(true); loadHistory(); };
  const closeHistory = () => { setHistoryOpen(false); setTrackedProvisionId(null); };

  const isEditing = editing !== null;
  const isNewRecord = isEditing && !editing?.ID;
  const hasDirty = dirtyFields.size > 0;
  const isPendingDelete = selected?.decommissionedAt != null;

  const filtered = databases.filter(db =>
    !searchTerm ||
    db.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    db.databaseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    db.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="stui-detail-empty"><Database size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Database size={20} />
          <h1>Databases</h1>
          <span className="stui-module-header-meta">{databases.length} instances</span>
        </div>
        <div className="stui-module-header-actions">
          {isEditing ? (
            <button
              className={`stui-crud-btn stui-delete-btn ${isNewRecord ? 'disabled' : ''}`}
              onClick={isNewRecord ? undefined : openHardDelete}
              disabled={saving || isNewRecord}
              title={isNewRecord ? 'Nothing to delete yet' : 'Delete'}
            ><Trash2 size={18} /></button>
          ) : (
            <button className="stui-crud-btn" onClick={startAdd} title="Provision new database"><Plus size={18} /></button>
          )}
          <button className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          <div className="stui-action-menu" ref={menuRef}>
            <button
              className="stui-action-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              title={anyProvisioningInFlight ? 'Actions — provisioning job in progress' : 'Actions'}
            >
              {anyProvisioningInFlight
                ? <Activity size={16} style={{ color: 'var(--color-primary-text)', animation: 'pulse 1.5s infinite' }} />
                : <GripVertical size={16} />}
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                <button className="stui-action-menu-item" onClick={openHistory}><History size={16} /><span>Provisioning History…</span></button>
                {selected && !isEditing && !isPendingDelete && (
                  <>
                    <div className="stui-action-menu-divider-strong" />
                    <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={handleDecommission}><Archive size={16} /><span>Decommission</span></button>
                  </>
                )}
                {selected && !isEditing && isPendingDelete && (
                  <>
                    <button className="stui-action-menu-item" onClick={handleRestore}><Undo2 size={16} /><span>Restore</span></button>
                    <div className="stui-action-menu-divider-strong" />
                    <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={openHardDelete}><Trash2 size={16} /><span>Permanently Delete</span></button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed"><button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button></div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input className="stui-field-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search databases..." />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">No databases found</div>
              ) : filtered.map(db => (
                <button key={db.id} onClick={() => { setSelected(db); setEditing(null); setDirtyFields(new Set()); }} className={`stui-list-item ${selected?.id === db.id ? 'selected' : ''}`}>
                  <span className="stui-list-item-primary">{db.displayName}</span>
                  {db.decommissionedAt ? (
                    <span className="stui-list-item-secondary"><span className="stui-badge-none">PENDING DELETE</span></span>
                  ) : (
                    <span className="stui-list-item-secondary">{db.description || db.databaseName}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="stui-detail-panel">
          {(isEditing || selected) ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">
                    {isEditing ? (isNewRecord ? 'New Database' : `Edit Database — ${selected?.databaseName}`) : 'Database Details'}
                  </div>
                </div>
                {selected && !isEditing && isPendingDelete && (
                  <>
                    <div className="stui-field-group stui-col-6">
                      <label>Status</label>
                      <div><span className="stui-badge-none">PENDING DELETE</span></div>
                    </div>
                    <div className="stui-field-group stui-col-6">
                      <label>Decommissioned At</label>
                      <input className="stui-field-input" value={formatTimestamp(selected.decommissionedAt)} readOnly />
                    </div>
                    <div className="stui-col-12" />
                  </>
                )}
                <div className="stui-field-group stui-col-12">
                  <label>Display Name</label>
                  {isEditing ? (
                    <input className={`stui-field-input${dirtyFields.has('DisplayName') ? ' dirty' : ''}`} value={editing.DisplayName} onChange={e => handleFieldChange('DisplayName', e.target.value)} autoFocus />
                  ) : (
                    <input className="stui-field-input" value={selected.displayName} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>Slug{isNewRecord ? ' (3–16, lowercase)' : ''}</label>
                  {isNewRecord ? (
                    <input className={`stui-field-input${dirtyFields.has('Slug') ? ' dirty' : ''}`} value={editing.Slug} onChange={e => handleFieldChange('Slug', e.target.value.toLowerCase())} placeholder="e.g. acme01" />
                  ) : (
                    <input className="stui-field-input" value={selected?.slug || '—'} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>Active</label>
                  {isEditing ? (
                    <StuiSelect className={`stui-field-input${dirtyFields.has('IsActive') ? ' dirty' : ''}`} value={editing.IsActive ? 'true' : 'false'} onChange={e => handleFieldChange('IsActive', e.target.value === 'true')}>
                      <option value="true">Yes</option><option value="false">No</option>
                    </StuiSelect>
                  ) : (
                    <input className="stui-field-input" value={selected.isActive ? 'Yes' : 'No'} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>SysAdmin Only</label>
                  {isEditing ? (
                    <StuiSelect className={`stui-field-input${dirtyFields.has('IsSysAdminOnly') ? ' dirty' : ''}`} value={editing.IsSysAdminOnly ? 'true' : 'false'} onChange={e => handleFieldChange('IsSysAdminOnly', e.target.value === 'true')}>
                      <option value="false">No</option><option value="true">Yes</option>
                    </StuiSelect>
                  ) : (
                    <input className="stui-field-input" value={selected.isSysAdminOnly ? 'Yes' : 'No'} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Database Name{isNewRecord ? ' (auto)' : ''}</label>
                  {isNewRecord ? (
                    <input className="stui-field-input" value={editing.Slug ? `${DB_NAME_PREFIX}${editing.Slug}` : ''} readOnly placeholder={`${DB_NAME_PREFIX}{slug}`} />
                  ) : (
                    <input className="stui-field-input" value={selected?.databaseName || ''} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Organization</label>
                  {isEditing ? (
                    <StuiSelect className={`stui-field-input${dirtyFields.has('OrganizationId') ? ' dirty' : ''}`} value={editing.OrganizationId || ''} onChange={e => handleFieldChange('OrganizationId', e.target.value ? parseInt(e.target.value, 10) : null)}>
                      <option value="">— None —</option>
                      {organizations.map(org => (<option key={org.id} value={org.id}>{org.displayName}</option>))}
                    </StuiSelect>
                  ) : (
                    <input className="stui-field-input" value={selected.organizationName || '—'} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Description</label>
                  {isEditing ? (
                    <input className={`stui-field-input${dirtyFields.has('Description') ? ' dirty' : ''}`} value={editing.Description} onChange={e => handleFieldChange('Description', e.target.value)} placeholder="What is this instance?" />
                  ) : (
                    <input className="stui-field-input" value={selected.description || ''} readOnly />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="stui-detail-empty"><Eye size={48} /><p>Select a database to view details</p><p className="stui-hint">Or click + to provision a new one</p></div>
          )}
        </div>
      </div>

      {/* HARD DELETE MODAL — kept for friction */}
      {hardDeleteOpen && hardDeleteTarget && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Permanently Delete Database</h3>
              <button className="stui-modal-close" onClick={closeHardDelete}><X size={20} /></button>
            </div>
            {!hardDeleteJob ? (
              <>
                <div className="stui-modal-body">
                  <div className="alert-warning">
                    <AlertTriangle size={16} className="alert-icon" />
                    <div className="alert-message">
                      This drops the Azure SQL database <strong>{hardDeleteTarget.databaseName}</strong> and removes it from the registry. Azure PITR retains backups for ~7 days but recovery is manual.
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Type the database name to confirm</label>
                    <input className="form-input" value={hardDeleteConfirm} onChange={e => setHardDeleteConfirm(e.target.value)} placeholder={hardDeleteTarget.databaseName} autoFocus />
                  </div>
                  {hardDeleteError && <div className="alert-error"><div className="alert-message">{hardDeleteError}</div></div>}
                </div>
                <div className="stui-modal-footer">
                  <button className="btn btn-secondary" onClick={closeHardDelete}>Cancel</button>
                  <button className="btn btn-primary" onClick={submitHardDelete} disabled={hardDeleteConfirm !== hardDeleteTarget.databaseName}>Permanently Delete</button>
                </div>
              </>
            ) : (
              <>
                <div className="stui-modal-body">
                  <div className="form-group"><label>Database</label><input className="form-input" value={hardDeleteJob.databaseName} readOnly /></div>
                  <div className="form-group">
                    <label>Status</label>
                    <div><span className={statusBadgeClass(hardDeleteJob.status)}>{hardDeleteJob.status}</span></div>
                  </div>
                  {hardDeleteJob.errorMessage && <div className="alert-error"><div className="alert-message">{hardDeleteJob.errorMessage}</div></div>}
                </div>
                <div className="stui-modal-footer">
                  {(hardDeleteJob.status === 'succeeded' || hardDeleteJob.status === 'failed') ? (
                    <button className="btn btn-primary" onClick={closeHardDelete}>Close</button>
                  ) : (
                    <button className="btn btn-secondary" onClick={closeHardDelete}>Close (job continues)</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* HISTORY MODAL — auto-opens after provision, also opened from kebab.
          Structural sizing inline per AuditLogModal precedent (bounded height, wider for the 6-col table). */}
      {historyOpen && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-xl">
            <div className="stui-modal-header">
              <h3>Provisioning History</h3>
              <button className="stui-modal-close" onClick={closeHistory}><X size={20} /></button>
            </div>
            <div className="stui-modal-body stui-modal-body-grid">
              {historyLoading ? (
                <div className="dev-detail-empty"><p>Loading…</p></div>
              ) : history.length === 0 ? (
                <div className="dev-detail-empty"><p>No history yet</p></div>
              ) : (
                <>
                  <div className="stui-data-grid-container">
                    <div className="stui-data-grid-scroll-area">
                      <table className="stui-data-grid stui-data-grid-auto">
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Action</th>
                            <th>Database</th>
                            <th>Status</th>
                            <th>By</th>
                            <th>Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map(r => (
                            <tr key={r.id}>
                              <td>{formatTimestamp(r.createdAt)}</td>
                              <td>{r.jobType}</td>
                              <td>{r.databaseName}</td>
                              <td><span className={statusBadgeClass(r.status)}>{r.status}</span></td>
                              <td>{r.requestedBy}</td>
                              <td>{r.errorMessage || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="stui-modal-footer">
              {anyProvisioningInFlight ? (
                <>
                  <span className="stui-status-dot stui-status-dot-live" />
                  <span style={{ color: 'var(--color-primary-text)' }}>Refreshing every {HISTORY_POLL_INTERVAL_MS / 1000}s</span>
                </>
              ) : (
                <>
                  <span className="stui-status-dot" />
                  <span>Idle</span>
                </>
              )}
              {historyLastChecked && <span className="stui-ml-auto">Last checked: {formatTimestamp(historyLastChecked)}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabasesPage;
